import { Button, Card, controlRing, cx, ErrorMessage, Skeleton, Switch } from "@studyward/ui";
import { ApiError } from "@studyward/shared";
import { X } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";

/** Owl + letter-spaced STUDYWARD with an ADMIN tag (web theme). A logotype: one image to assistive tech. */
export function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <span role="img" aria-label="StudyWard Admin" className="inline-flex items-center gap-2 text-text-primary">
      <img src="/owl-head.webp" alt="" width={compact ? 30 : 36} height={compact ? 30 : 36} />
      <span aria-hidden="true" className={cx("font-body font-bold uppercase", compact ? "text-small tracking-wider" : "text-h2 tracking-widest")}>
        Study<span className="text-text-link">Ward</span>
      </span>
      <span aria-hidden="true" className="rounded-pill bg-text-primary px-2 py-0.5 text-caption font-bold uppercase tracking-wider text-surface-background">
        Admin
      </span>
    </span>
  );
}

export const INTERNAL_NOTE = "Internal use only. This console contains student data and access is logged. StudyWard is a study aid, not medical advice.";

/** Sign-in frame: soft Titanium panel, crisp white card (same look as the web app's auth pages). */
export function AuthFrame({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-dvh p-3 sm:p-4 lg:p-5">
      <div className="relative flex w-full flex-col items-center rounded-card bg-surface-hero px-4 pb-10 pt-24 md:rounded-card-lg md:pt-28">
        <div className="absolute left-6 top-6 md:left-8 md:top-8">
          <Wordmark />
        </div>
        <main className="my-auto w-full max-w-md rounded-card bg-surface-card p-8 shadow-raised md:p-10">
          <h1 className="text-h1 font-medium tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-small text-text-secondary">{subtitle}</p>}
          <div className="mt-6 flex flex-col gap-4">{children}</div>
        </main>
        <p className="mt-6 max-w-md text-center text-caption text-text-primary">{INTERNAL_NOTE}</p>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end gap-x-4 gap-y-3">
      {/* Text takes the full row on phones so actions drop below it instead of squeezing it. */}
      <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
        <h1 className="text-h1 font-medium tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 max-w-3xl text-small text-text-secondary">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function SectionTitle({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-start gap-x-4 gap-y-2">
      <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
        <h2 className="text-h2 font-semibold">{title}</h2>
        {subtitle && <p className="mt-1 text-small text-text-secondary">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

/** KPI tile: uppercase label, big number, optional note (trend, SLA). */
export function StatCard({ label, value, note, tone = "plain", className, children }: { label: string; value: ReactNode; note?: ReactNode; tone?: "plain" | "warning"; className?: string; children?: ReactNode }) {
  return (
    <Card className={cx("flex flex-col gap-1.5 p-5", tone === "warning" && "bg-warning-soft", className)}>
      <p className="text-caption font-bold uppercase tracking-wider text-text-secondary">{label}</p>
      <p className="font-display text-h1 font-medium leading-none tabular-nums">{value}</p>
      {note && <p className="text-caption font-semibold text-text-secondary">{note}</p>}
      {children}
    </Card>
  );
}

const PILL_TONES = {
  neutral: "bg-surface-subtle text-text-primary",
  info: "bg-primary-soft text-text-primary",
  success: "bg-success-soft text-success-text",
  warning: "bg-warning-soft text-warning-text",
  danger: "bg-danger-soft text-danger-text",
} as const;
export type PillTone = keyof typeof PILL_TONES;

export function StatusPill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return <span className={cx("inline-flex items-center whitespace-nowrap rounded-pill px-2.5 py-0.5 text-caption font-bold", PILL_TONES[tone])}>{children}</span>;
}

/** On/off switch (role="switch"); the shared Switch, under the admin name. */
export const Toggle = Switch;

/** Number field for small integer settings (limits, set sizes, days). */
export function NumberField({ label, hint, value, onChange, min, max, suffix, disabled }: { label: string; hint?: string; value: number; onChange: (v: number) => void; min: number; max: number; suffix?: string; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border py-3 last:border-0">
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="text-small font-semibold">
          {label}
        </label>
        {hint && <p className="text-caption text-text-secondary">{hint}</p>}
      </div>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={Number.isFinite(value) ? value : ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === "" ? Number.NaN : Number(e.target.value))}
          className={cx("min-h-11 w-24 rounded-control bg-surface-background px-3 text-right text-body tabular-nums disabled:bg-surface-subtle disabled:text-text-muted", controlRing())}
        />
        {/* Fixed suffix slot so fields with and without a unit line up on the right. */}
        <span className="w-8 text-small text-text-secondary">{suffix}</span>
      </div>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-card bg-surface-subtle px-6 py-10 text-center">
      <p className="text-h2 font-semibold">{title}</p>
      {children && <div className="mx-auto mt-2 max-w-lg text-small text-text-secondary">{children}</div>}
    </div>
  );
}

/** Loading / error wrapper for one query: skeleton, error with retry, or the content. */
export function QueryState({ query, label, skeleton = "h-48", children }: { query: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown }; label: string; skeleton?: string; children: () => ReactNode }) {
  if (query.isPending) return <Skeleton className={cx("w-full", skeleton)} label={`Loading ${label}`} />;
  if (query.isError) return <ErrorMessage message={errorText(query.error)} onRetry={() => void query.refetch()} />;
  return <>{children()}</>;
}

export function errorText(err: unknown): string {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

/** Modal confirmation for consequential actions (suspend, delete, publish — §11.2). Uses the native <dialog>. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  danger,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-card bg-surface-background p-0 text-text-primary shadow-raised backdrop:bg-text-primary/40"
    >
      <div className="flex flex-col gap-4 p-6">
        <h2 id={titleId} className="text-h2 font-semibold">
          {title}
        </h2>
        <div className="text-small text-text-secondary">{children}</div>
        {error && <ErrorMessage message={error} />}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant={danger ? "danger" : "primary"} size="sm" onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}

/** Right-hand detail panel (user record). A modal <dialog> so focus stays inside until it closes. */
export function Drawer({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      className="ml-auto mr-0 h-dvh max-h-dvh w-full max-w-xl bg-surface-background p-0 text-text-primary shadow-raised backdrop:bg-text-primary/40 md:rounded-l-card-lg"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-3 border-b border-border p-6">
          <h2 id={titleId} className="min-w-0 flex-1 truncate text-h2 font-semibold">
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="grid size-11 place-items-center rounded-pill hover:bg-surface-subtle">
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------- formatting
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usdWhole = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const num = new Intl.NumberFormat("en-US");
export const money = (v: number, whole = false) => (whole ? usdWhole : usd).format(v);
export const count = (v: number) => num.format(v);

/** True when the timestamp is still ahead (quota pauses, scheduled retries). */
export const isFuture = (iso: string | null | undefined) => !!iso && Date.parse(iso) > Date.now();

export function ago(iso: string | null | undefined): string {
  if (!iso) return "—";
  const s = Math.round((Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 172_800) return "yesterday";
  if (s < 30 * 86_400) return `${Math.floor(s / 86_400)} days ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export const dateTime = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
export const shortDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function duration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${String(Math.round(seconds % 60)).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
}

/** Percentage change label, e.g. "▲ 8.2% vs last month". */
export function trend(now: number, before: number, period: string): string | undefined {
  if (before <= 0) return undefined;
  const pct = ((now - before) / before) * 100;
  return `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct).toFixed(1)}% vs ${period}`;
}
